import {
    ConcessionMessageStatus,
    ConcessionMessageType,
} from '../../models/Concession'
import { toYYYYMMDD } from '../../helpers/date'

/**
 * Parse a `YYYY-MM-DD` API string into a local Date (midnight local time).
 * Avoids the UTC shift that `new Date('YYYY-MM-DD')` introduces.
 */
export const parseApiDate = (value?: string): Date | null => {
    if (!value) return null
    const [year, month, day] = value.split('-').map((p) => parseInt(p, 10))
    if (!year || !month || !day) return null
    return new Date(year, month - 1, day)
}

/** Add (or subtract) days to a `YYYY-MM-DD` string, returning `YYYY-MM-DD`. */
export const addDaysToApiDate = (value: string, days: number): string => {
    const date = parseApiDate(value)
    if (!date) return ''
    date.setDate(date.getDate() + days)
    return toYYYYMMDD(date)
}

/**
 * The offer's inclusive deadline: `start_date + (duration_days - 1)` days.
 * Mirrors the backend's computed `end_date`. Returns '' if inputs invalid.
 */
export const computeEndDate = (
    startDate: string,
    durationDays: number,
): string => {
    if (!startDate || !durationDays || durationDays < 1) return ''
    return addDaysToApiDate(startDate, durationDays - 1)
}

/** Display a `YYYY-MM-DD` API string as `MM/DD/YYYY`. */
export const displayApiDate = (value?: string): string => {
    const date = parseApiDate(value)
    if (!date) return '—'
    return date.toLocaleDateString('en-US')
}

/** Display a `YYYY-MM-DD` API string as `Jan 30, 2026`. */
export const displayLongDate = (value?: string): string => {
    const date = parseApiDate(value)
    if (!date) return '—'
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export const MESSAGE_TYPE_OPTIONS: {
    id: number
    value: ConcessionMessageType
    label: string
}[] = [
    { id: 0, value: 'email', label: 'Email' },
    { id: 1, value: 'text', label: 'Text message' },
]

/** Statuses staff can set (`sent` is system-only). */
export const MESSAGE_STATUS_OPTIONS: {
    id: number
    value: Exclude<ConcessionMessageStatus, 'sent'>
    label: string
}[] = [
    { id: 0, value: 'scheduled', label: 'Scheduled' },
    { id: 1, value: 'draft', label: 'Draft' },
    { id: 2, value: 'canceled', label: 'Canceled' },
]

export const messageStatusLabel = (status: ConcessionMessageStatus): string => {
    switch (status) {
        case 'scheduled':
            return 'Scheduled'
        case 'draft':
            return 'Draft'
        case 'canceled':
            return 'Canceled'
        case 'sent':
            return 'Sent'
        default:
            return status
    }
}

/** Display an ISO 8601 datetime as a short local date/time. */
export const displayDateTime = (iso?: string | null): string => {
    if (!iso) return '—'
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return '—'
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}
